import { FC, useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CircularProgress,
  Alert,
  Button,
  TablePagination,
} from '@mui/material';
import {
  IconChevronUp,
  IconChevronDown,
  IconEye,
  IconClipboardList,
} from '@tabler/icons-react';
import useGetPartnerBookings from '../../../hooks/useGetPartnerBookings';
import { getErrorMessage } from '../../../utils/errorUtils';
import { format, parseISO } from 'date-fns';
import BookingStatusBadge from './components/BookingStatusBadge';
import BookingFilters from './components/BookingFilters';
import BookingCard from './components/BookingCard';
import { Dayjs } from 'dayjs';

type SortField =
  | 'bookingId'
  | 'boatName'
  | 'routeName'
  | 'embarkationDate'
  | 'disembarkationDate'
  | 'guestCount'
  | 'bookingStatus';
type SortDirection = 'asc' | 'desc';

const BookingsList: FC = () => {
  const navigate = useNavigate();
  const {
    bookings,
    isLoading,
    isError,
    error: errorDetails,
  } = useGetPartnerBookings();

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<number | 'all'>('all');
  const [boatFilter, setBoatFilter] = useState<number | 'all'>('all');
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);

  // Sort states
  const [sortField, setSortField] = useState<SortField>('embarkationDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [viewMode, setViewMode] = useState<'table' | 'card'>('table');

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Filter bookings
  const filteredBookings = useMemo(() => {
    let filtered = [...bookings];

    // Search filter
    if (debouncedSearchQuery.trim()) {
      const query = debouncedSearchQuery.toLowerCase();
      filtered = filtered.filter(
        booking =>
          booking.bookingId.toString().includes(query) ||
          booking.boatName.toLowerCase().includes(query) ||
          booking.routeName?.toLowerCase().includes(query) ||
          false, // Guest name search would need additional data
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(
        booking => booking.bookingStatus === statusFilter,
      );
    }

    // Boat filter
    if (boatFilter !== 'all') {
      filtered = filtered.filter(booking => booking.boatId === boatFilter);
    }

    // Date range filter
    if (startDate) {
      const start = startDate.toDate();
      filtered = filtered.filter(booking => {
        const embarkDate = parseISO(booking.embarkationDate);
        return embarkDate >= start;
      });
    }

    if (endDate) {
      const end = endDate.toDate();
      filtered = filtered.filter(booking => {
        const embarkDate = parseISO(booking.embarkationDate);
        return embarkDate <= end;
      });
    }

    return filtered;
  }, [
    bookings,
    debouncedSearchQuery,
    statusFilter,
    boatFilter,
    startDate,
    endDate,
  ]);

  // Sort bookings
  const sortedBookings = useMemo(() => {
    const sorted = [...filteredBookings];
    sorted.sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortField) {
        case 'bookingId':
          aValue = a.bookingId;
          bValue = b.bookingId;
          break;
        case 'boatName':
          aValue = a.boatName.toLowerCase();
          bValue = b.boatName.toLowerCase();
          break;
        case 'routeName':
          aValue = (a.routeName || '').toLowerCase();
          bValue = (b.routeName || '').toLowerCase();
          break;
        case 'embarkationDate':
          aValue = new Date(a.embarkationDate).getTime();
          bValue = new Date(b.embarkationDate).getTime();
          break;
        case 'disembarkationDate':
          aValue = new Date(a.disembarkationDate).getTime();
          bValue = new Date(b.disembarkationDate).getTime();
          break;
        case 'guestCount':
          aValue = a.guestCount;
          bValue = b.guestCount;
          break;
        case 'bookingStatus':
          aValue = a.bookingStatus;
          bValue = b.bookingStatus;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredBookings, sortField, sortDirection]);

  // Paginate bookings
  const paginatedBookings = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return sortedBookings.slice(start, end);
  }, [sortedBookings, page, rowsPerPage]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleClearAllFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setBoatFilter('all');
    setStartDate(null);
    setEndDate(null);
    setPage(0); // Reset to first page when clearing filters
  };

  const SortButton: FC<{ field: SortField; children: React.ReactNode }> = ({
    field,
    children,
  }) => (
    <button
      onClick={() => handleSort(field)}
      className="flex items-center gap-1 hover:text-gray-900"
    >
      {children}
      {sortField === field &&
        (sortDirection === 'asc' ? (
          <IconChevronUp className="h-4 w-4" />
        ) : (
          <IconChevronDown className="h-4 w-4" />
        ))}
    </button>
  );

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <CircularProgress />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mx-auto max-w-7xl">
        <Alert severity="error" className="mb-4">
          <div className="font-semibold">Error loading bookings</div>
          <div className="mt-2 text-sm">{getErrorMessage(errorDetails)}</div>
        </Alert>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Bookings</h1>
        <p className="mt-1 text-sm text-gray-500">
          View and manage all your bookings
        </p>
      </div>

      {/* Filters */}
      <BookingFilters
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        boatFilter={boatFilter}
        onBoatChange={setBoatFilter}
        startDate={startDate}
        onStartDateChange={setStartDate}
        endDate={endDate}
        onEndDateChange={setEndDate}
        onClearAll={handleClearAllFilters}
      />

      {/* View Mode Toggle and Results Count */}
      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          Showing {paginatedBookings.length} of {filteredBookings.length}{' '}
          booking{filteredBookings.length !== 1 ? 's' : ''}
          {filteredBookings.length !== bookings.length &&
            ` (filtered from ${bookings.length} total)`}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'table' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setViewMode('table')}
          >
            Table
          </Button>
          <Button
            variant={viewMode === 'card' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setViewMode('card')}
          >
            Cards
          </Button>
        </div>
      </div>

      {filteredBookings.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
          <IconClipboardList className="mx-auto h-16 w-16 text-gray-400" />
          {bookings.length === 0 ? (
            <>
              <p className="mt-4 text-gray-500">No bookings found</p>
              <p className="mt-2 text-sm text-gray-400">
                Bookings will appear here once customers make reservations
              </p>
            </>
          ) : (
            <>
              <p className="mt-4 text-gray-500">
                No bookings match your filters
              </p>
              <p className="mt-2 text-sm text-gray-400">
                Try adjusting your search criteria
              </p>
            </>
          )}
        </div>
      ) : viewMode === 'table' ? (
        <>
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <SortButton field="bookingId">Booking ID</SortButton>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <SortButton field="boatName">Boat Name</SortButton>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <SortButton field="routeName">Route Name</SortButton>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <SortButton field="embarkationDate">
                      Embarkation Date
                    </SortButton>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <SortButton field="disembarkationDate">
                      Disembarkation Date
                    </SortButton>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <SortButton field="guestCount">Guest Count</SortButton>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <SortButton field="bookingStatus">Status</SortButton>
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {paginatedBookings.map(booking => (
                  <tr key={booking.bookingId} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                      {booking.bookingId}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {booking.leadGuestFullName ? (
                        <div>
                          <div className="font-medium text-gray-900">
                            {booking.leadGuestFullName}
                          </div>
                          {booking.leadGuestEmail && (
                            <div className="text-xs text-gray-500">
                              {booking.leadGuestEmail}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">N/A</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {booking.boatName}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {booking.routeName || 'N/A'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {format(
                        new Date(booking.embarkationDate),
                        'MMM dd, yyyy',
                      )}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {format(
                        new Date(booking.disembarkationDate),
                        'MMM dd, yyyy',
                      )}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {booking.guestCount}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm">
                      <BookingStatusBadge status={booking.bookingStatus} />
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                      <button
                        onClick={() =>
                          navigate(`/partner/bookings/${booking.bookingId}`)
                        }
                        className="flex items-center gap-1 text-blue-600 hover:text-blue-900"
                      >
                        <IconEye className="h-4 w-4" />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={filteredBookings.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            className="border-t border-gray-200 bg-white"
          />
        </>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {paginatedBookings.map(booking => (
              <BookingCard key={booking.bookingId} booking={booking} />
            ))}
          </div>

          {/* Pagination for card view */}
          <div className="mt-4">
            <TablePagination
              component="div"
              count={filteredBookings.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={e => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              className="border-t border-gray-200 bg-white"
            />
          </div>
        </>
      )}
    </div>
  );
};

export default BookingsList;
